<template>
    <div class="dock-music-icon">
        <div class="dock-music-inner" :class="[effectClass]">
            <div class="dock-music-bg"></div>

            <template v-if="dockAnimation">
                <div v-if="coverUrl" class="dock-music-cover">
                    <img :src="coverUrl" alt=""/>
                </div>
                <div v-else class="dock-music-cover-fallback">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/>
                    </svg>
                </div>

                <div class="dock-music-hero">
                    <div v-if="hasSource" class="dock-music-eq">
                        <span v-for="i in 4" :key="i" class="dock-music-eq-bar" :class="{'dock-music-eq-bar--paused': !ms.state.playing}"></span>
                    </div>
                    <div v-else class="dock-music-note-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/>
                        </svg>
                    </div>
                </div>

                <div class="dock-music-text-wrap" ref="textWrap">
                    <transition name="dock-music-text-fade" mode="out-in" @after-enter="checkScroll">
                        <span class="dock-music-text-cycle" :key="cycleKey">
                            <span class="dock-music-text" ref="textEl">{{ cycleText }}</span>
                        </span>
                    </transition>
                </div>

                <div v-if="hasSource && progress > 0" class="dock-music-progress">
                    <div class="dock-music-progress-fill" :style="{width: progress + '%'}"></div>
                </div>
            </template>
            <template v-else>
                <div class="dock-music-hero">
                    <div class="dock-music-note-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/>
                        </svg>
                    </div>
                </div>
                <div class="dock-music-text-wrap">
                    <span class="dock-music-text-cycle">
                        <span class="dock-music-text">{{ $t('common.music.title') }}</span>
                    </span>
                </div>
            </template>
        </div>
    </div>
</template>

<script>
import musicService from './musicService'

export default {
    name: 'DockMusicIcon',
    data() {
        return {
            cycleIndex: 0,
        }
    },
    created() {
        this.cycleTimer = null
    },
    computed: {
        ms() { return musicService },
        dockAnimation() {
            return musicService.state.dockAnimation
        },
        hasSource() {
            return !!(musicService.state.currentStation || musicService.state.currentTrack)
        },
        coverUrl() {
            const t = musicService.state.currentTrack
            if (t?.hasCover && t?.thumb?.small) return t.thumb.small
            const s = musicService.state.currentStation
            if (s?.favicon) return s.favicon
            return null
        },
        effectClass() {
            if (!this.hasSource) return 'dock-music--idle'
            if (musicService.state.playing) return 'dock-music--playing'
            return 'dock-music--paused'
        },
        progress() {
            if (!musicService.state.currentTrack) return 0
            const d = musicService.state.duration
            if (!d) return 0
            return (musicService.state.currentTime / d) * 100
        },
        cycleTexts() {
            if (!this.hasSource) return [this.$t('common.music.title')]
            const texts = []
            const name = musicService.displayName.value
            if (name) texts.push(name)
            const sub = musicService.displaySubtext.value
            if (sub) texts.push(sub)
            if (!texts.length) texts.push(this.$t('common.music.title'))
            return texts
        },
        cycleText() {
            return this.cycleTexts[this.cycleIndex % this.cycleTexts.length]
        },
        cycleKey() {
            return this.cycleIndex + '_' + this.cycleText
        },
    },
    methods: {
        startCycle() {
            this.stopCycle()
            if (this.cycleTexts.length <= 1) return
            this._cycling = true
            this._scheduleNext(3000)
        },
        _scheduleNext(delay) {
            if (!this._cycling) return
            this.cycleTimer = setTimeout(() => {
                this.cycleIndex = (this.cycleIndex + 1) % this.cycleTexts.length
            }, delay)
        },
        stopCycle() {
            this._cycling = false
            clearTimeout(this.cycleTimer)
            this.cycleTimer = null
        },
        checkScroll() {
            const el = this.$refs.textEl
            if (!el) return
            el.classList.remove('dock-music-text--scroll')
            void el.offsetWidth
            const parent = this.$refs.textWrap
            if (!parent) return
            if (el.scrollWidth > parent.clientWidth + 1) {
                const dist = el.scrollWidth - parent.clientWidth
                const dur = Math.max(2.5, dist / 18)
                el.style.setProperty('--scroll-dist', -dist + 'px')
                el.style.setProperty('--scroll-dur', dur + 's')
                el.addEventListener('animationend', () => {
                    if (this.cycleTexts.length > 1) this._scheduleNext(800)
                }, { once: true })
                requestAnimationFrame(() => {
                    el.classList.add('dock-music-text--scroll')
                })
                return
            }
            if (this.cycleTexts.length > 1) {
                this._scheduleNext(3000)
            }
        },
    },
    watch: {
        hasSource(val) {
            if (val && this.dockAnimation) this.startCycle()
            else {
                this.stopCycle()
                this.cycleIndex = 0
            }
        },
        dockAnimation(val) {
            if (val && this.hasSource) this.startCycle()
            else {
                this.stopCycle()
                this.cycleIndex = 0
            }
        },
        'cycleTexts.length'() {
            this.cycleIndex = 0
            if (this.hasSource && this.dockAnimation) this.startCycle()
        },
    },
    mounted() {
        musicService.init()
        if (this.hasSource && this.dockAnimation) this.startCycle()
    },
    beforeUnmount() {
        this.stopCycle()
    },
}
</script>

<style scoped>
.dock-music-icon {
    width: 100%;
    height: 100%;
    overflow: visible;
    position: relative;
}

.dock-music-icon span {
    display: block;
}

.dock-music-inner {
    width: 100%;
    height: 100%;
    border-radius: 12px;
    overflow: hidden;
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}

.dock-music-bg {
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, #7C3AED 0%, #4C1D95 100%);
    transition: background 0.6s ease;
}

.dock-music--playing .dock-music-bg {
    background: linear-gradient(135deg, #8B5CF6 0%, #5B21B6 100%);
}

.dock-music--paused .dock-music-bg {
    background: linear-gradient(135deg, #6D28D9 0%, #3B0764 100%);
}

.dock-music-cover {
    position: absolute;
    inset: 0;
    z-index: 0;
}

.dock-music-cover img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    filter: blur(3px) brightness(0.5);
    transform: scale(1.1);
}

.dock-music--playing .dock-music-cover img {
    filter: blur(2px) brightness(0.55);
}

.dock-music--paused .dock-music-cover img {
    filter: blur(5px) brightness(0.35);
}

.dock-music-cover-fallback {
    position: absolute;
    inset: 0;
    z-index: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0.08;
}

.dock-music-cover-fallback svg {
    width: 80%;
    height: 80%;
    color: #fff;
}

.dock-music--playing .dock-music-cover-fallback {
    opacity: 0.12;
}

.dock-music-hero {
    position: relative;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    height: calc(var(--dock-icon-size, 48px) * 0.25);
    margin-top: calc(var(--dock-icon-size, 48px) * 0.08);
}

.dock-music-eq {
    display: flex;
    align-items: flex-end;
    justify-content: center;
    gap: calc(var(--dock-icon-size, 48px) * 0.035);
    height: 100%;
}

.dock-music-eq-bar {
    width: calc(var(--dock-icon-size, 48px) * 0.055);
    background: rgba(255, 255, 255, 0.9);
    border-radius: calc(var(--dock-icon-size, 48px) * 0.02);
    animation: dock-music-eq 0.6s ease-in-out infinite alternate;
}

.dock-music-eq-bar:nth-child(1) { height: 35%; animation-delay: 0s; }
.dock-music-eq-bar:nth-child(2) { height: 75%; animation-delay: 0.15s; }
.dock-music-eq-bar:nth-child(3) { height: 50%; animation-delay: 0.07s; }
.dock-music-eq-bar:nth-child(4) { height: 85%; animation-delay: 0.22s; }
.dock-music-eq-bar:nth-child(5) { height: 40%; animation-delay: 0.1s; }

.dock-music-eq-bar--paused {
    animation-play-state: paused;
    height: 15% !important;
    transition: height 0.3s ease;
}

@keyframes dock-music-eq {
    0% { height: 15%; }
    100% { height: 100%; }
}

.dock-music-note-icon {
    color: rgba(255, 255, 255, 0.75);
    width: calc(var(--dock-icon-size, 48px) * 0.36);
    height: calc(var(--dock-icon-size, 48px) * 0.36);
}

.dock-music-note-icon svg {
    width: 100%;
    height: 100%;
    display: block;
}

.dock-music-text-wrap {
    position: relative;
    z-index: 1;
    width: 82%;
    overflow: hidden;
    clip-path: inset(-4px 0);
    height: calc(var(--dock-icon-size, 48px) * 0.2);
    display: flex;
    align-items: center;
    justify-content: flex-start;
    margin-top: calc(var(--dock-icon-size, 48px) * 0.10);
}

.dock-music-text-cycle {
    margin: 0 auto;
    text-align: center;
}

.dock-music-text {
    font-size: calc(var(--dock-icon-size, 48px) * 0.16);
    font-weight: 600;
    color: rgba(255, 255, 255, 0.9);
    text-shadow: 0 1px 3px rgba(0, 0, 0, 0.6);
    white-space: nowrap;
    display: inline-block;
    line-height: 1.2;
}

.dock-music--idle .dock-music-text {
    font-size: calc(var(--dock-icon-size, 48px) * 0.2);
}


.dock-music-text--scroll {
    animation: dockMusicTextScroll var(--scroll-dur, 2.2s) ease-in-out;
}

@keyframes dockMusicTextScroll {
    0%, 12% { transform: translateX(0); }
    45%, 55% { transform: translateX(var(--scroll-dist)); }
    88%, 100% { transform: translateX(0); }
}

.dock-music-text-fade-enter-active,
.dock-music-text-fade-leave-active {
    transition: opacity 0.35s ease, transform 0.35s ease;
}

.dock-music-text-fade-enter-from {
    opacity: 0;
    transform: translateY(20%);
}

.dock-music-text-fade-leave-to {
    opacity: 0;
    transform: translateY(-20%);
}

.dock-music-progress {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: calc(var(--dock-icon-size, 48px) * 0.04);
    z-index: 2;
    background: rgba(255, 255, 255, 0.15);
}

.dock-music-progress-fill {
    height: 100%;
    background: rgba(255, 255, 255, 0.7);
    transition: width 0.5s linear;
    border-radius: 0 1px 1px 0;
}
</style>
